import { useMemo, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  PieChart, Pie, Cell, BarChart, Bar, XAxis, ResponsiveContainer, Tooltip,
} from "recharts";
import {
  FolderKanban, ClipboardList, FileText, Banknote, ChevronRight,
  Calendar, CalendarDays, AlertTriangle, Plus, Activity,
  Building2, Truck, Package, Wrench, ShieldCheck, Users, ArrowRight,
  TrendingUp, MapPin, Map as MapIcon, LayoutGrid, Clock, Inbox,
} from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useRequireAuth, hasRole } from "@/lib/auth";
import { Card, Pill, KPI, Button, Spinner, EmptyState } from "@/components/ui";
import { format, parseISO, formatDistanceToNow, isToday, isTomorrow, addDays, startOfDay } from "date-fns";
import SitesMapWidget from "./SitesMapWidget";

// =============================================================================
// ProjectHubScreen — /project (T-071 Project portal polish, Phase A)
//
// Replaces the generic HubScreen for the Project hub with a director/manager
// dashboard that gives a "command centre" feel: cross-project KPIs, this
// week's bookings, active project cards with status, project-status donut
// chart, and a recent activity feed.
//
// Field/subbie roles never land here (their nav doesn't include /project).
// =============================================================================

type ProjectStatus =
  | "bid" | "quoted" | "won" | "mobilising" | "live"
  | "on_hold" | "acceptance_pending" | "closed" | "cancelled" | "lost";

const STATUS_TONE: Record<ProjectStatus, "live" | "mobilising" | "acceptance_pending" | "on_hold" | "closed" | "cancelled" | "bid" | "quoted" | "won" | "lost"> = {
  bid:                "bid",
  quoted:             "quoted",
  won:                "won",
  mobilising:         "mobilising",
  live:               "live",
  on_hold:            "on_hold",
  acceptance_pending: "acceptance_pending",
  closed:             "closed",
  cancelled:          "cancelled",
  lost:               "lost",
};

const STATUS_LABEL: Record<ProjectStatus, string> = {
  bid: "BID", quoted: "quoted", won: "won",
  mobilising: "mobilising", live: "live", on_hold: "on hold",
  acceptance_pending: "acceptance pending", closed: "closed",
  cancelled: "cancelled", lost: "lost",
};

const ACTIVE_STATUSES: ProjectStatus[] = ["mobilising", "live", "acceptance_pending"];

// -----------------------------------------------------------------------------

export default function ProjectHubScreen() {
  const user = useRequireAuth();
  const canManage = hasRole(user, "manager");

  // T-154 — Supplier subbies (role=subcontractor) have no project visibility.
  if (user?.role === "subcontractor") {
    return <Navigate to="/profile" replace />;
  }

  // #36 — Field + supervisor don't get the company-wide Project hub.
  // Their "Project" tab routes here from the top nav, but the right view
  // for them is their own assigned projects list (FieldProjectsListScreen
  // renders at /projects with team + crew filtering already in place).
  // Stops the leak where they could see and click into projects they
  // weren't assigned to.
  if (user?.role === "field" || user?.role === "supervisor") {
    return <Navigate to="/projects" replace />;
  }

  // -- KPIs --------------------------------------------------------------
  const { data: kpis } = useQuery({
    queryKey: ["project_hub_kpis"],
    queryFn: async () => {
      const weekFromNow = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString();

      const [
        activeRes, bidsRes, quotesRes, bookingsRes,
        hopsRes, apRes, allProjectsRes,
      ] = await Promise.all([
        supabase.from("projects").select("project_id", { count: "exact", head: true })
          .in("status", ACTIVE_STATUSES),
        supabase.from("bids").select("bid_id", { count: "exact", head: true })
          .in("status", ["open", "submitted", "in_progress"]),
        supabase.from("quotes").select("quote_id", { count: "exact", head: true })
          .in("status", ["sent", "pending_approval"]),
        supabase.from("project_bookings").select("booking_id", { count: "exact", head: true })
          .gte("scheduled_start", new Date().toISOString())
          .lte("scheduled_start", weekFromNow)
          .not("status", "in", "(completed,cancelled)"),
        supabase.from("hop_packs").select("pack_id", { count: "exact", head: true })
          .eq("status", "submitted"),
        supabase.from("pending_receipts").select("receipt_id", { count: "exact", head: true })
          .eq("status", "new"),
        // For donut chart — full distribution
        supabase.from("projects").select("status"),
      ]);

      // Build status distribution from raw rows
      const statusCounts = new Map<ProjectStatus, number>();
      for (const row of (allProjectsRes.data ?? []) as Array<{ status: ProjectStatus }>) {
        statusCounts.set(row.status, (statusCounts.get(row.status) ?? 0) + 1);
      }

      return {
        active:    activeRes.count ?? 0,
        bids:      bidsRes.count ?? 0,
        quotes:    quotesRes.count ?? 0,
        bookings:  bookingsRes.count ?? 0,
        hops:      hopsRes.count ?? 0,
        apBacklog: apRes.count ?? 0,
        statusDistribution: Array.from(statusCounts.entries())
          .map(([status, count]) => ({ status, count })),
      };
    },
  });

  // -- This week's upcoming bookings (top 5) ------------------------------
  const { data: upcomingBookings = [] } = useQuery({
    queryKey: ["project_hub_upcoming"],
    queryFn: async () => {
      const { data } = await supabase
        .from("v_upcoming_bookings")
        .select("*")
        .lte("scheduled_start", new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString())
        .order("scheduled_start")
        .limit(6);
      return data ?? [];
    },
  });

  // -- Active projects -----------------------------------------------------
  // James, 22 Sep 2026: "Filter buttons in widget for quick filter on projects
  // as this gets bigger." The widget used to fetch 8 rows and show all 8, so
  // filtering client-side would have filtered a sample rather than the list.
  // It now fetches every active project (there are tens, not thousands) and
  // shows the first 8 of whichever status is selected.
  const [projFilter, setProjFilter] = useState<"all" | ProjectStatus>("all");

  const { data: activeProjects = [] } = useQuery({
    queryKey: ["project_hub_active"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("projects")
        .select(`project_id, project_code, project_name, status, updated_at,
                 client:client_id(legal_name),
                 carrier:carrier_id(carrier_code),
                 site:site_id(site_code, suburb)`)
        .in("status", ACTIVE_STATUSES)
        .order("updated_at", { ascending: false })
        .limit(200);
      if (error) throw error;
      return data ?? [];
    },
  });

  // Counts drive the chip labels, so a chip tells you how many are behind it
  // before you press it. Counted off the same rows the list is filtered from.
  const activeCounts = useMemo(() => {
    const m = new Map<ProjectStatus, number>();
    for (const p of activeProjects as any[]) {
      m.set(p.status, (m.get(p.status) ?? 0) + 1);
    }
    return m;
  }, [activeProjects]);

  const filteredActive = useMemo(
    () => (projFilter === "all"
      ? (activeProjects as any[])
      : (activeProjects as any[]).filter(p => p.status === projFilter)
    ).slice(0, 8),
    [activeProjects, projFilter],
  );

  if (!user) return null;

  const totalProjects = kpis?.statusDistribution.reduce((s, r) => s + r.count, 0) ?? 0;

  // Container — full viewport width on wide monitors per James 2026-04-25.
  // No max-w cap; the only horizontal constraint is the px gutter from edge.
  // Per-component widths (map, charts) handle their own sensible caps.
  return (
    <div className="px-6 md:px-10 py-6 md:py-8 pb-20">
      {/* Header */}
      <div className="mb-5 flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-3 mb-1">
            <div className="h-1 w-10 bg-accent rounded-full" />
            <div className="text-text-muted text-[11px] tracking-wider2 uppercase font-mono">Project portal</div>
          </div>
          <h1 className="text-[24px] md:text-[28px] font-semibold tracking-tight flex items-center gap-2">
            <FolderKanban size={24} className="text-accent" />
            Command Centre
          </h1>
          <p className="text-text-muted text-[13px] mt-1">
            {totalProjects > 0
              ? <>Tracking <span className="text-text font-mono">{totalProjects}</span> project{totalProjects === 1 ? "" : "s"} — {kpis?.active ?? 0} active.</>
              : <>No projects yet — start by creating a BID or jump straight to a project.</>}
          </p>
        </div>
        {canManage && (
          <div className="flex items-center gap-2 flex-wrap">
            <Link to="/bids">
              <Button variant="secondary" size="sm" icon={ClipboardList}>BIDs / RFQs</Button>
            </Link>
            {/* mig 451 — an RFQ lands in the intake first, then becomes bids */}
            <Link to="/project/rfq-intake">
              <Button variant="secondary" size="sm" icon={Inbox}>RFQ intake</Button>
            </Link>
            <Link to="/projects/new">
              <Button variant="primary" size="sm" icon={Plus}>New project</Button>
            </Link>
          </div>
        )}
      </div>

      {/* Project portal nav — quick-jump tiles to all project surfaces.
          Charcoal + orange per James 2026-04-25 — clear visual nav without
          forcing users to open the sidebar drawer. */}
      <div className="mb-6">
        <div className="text-text-muted text-[10px] tracking-wider2 uppercase font-mono mb-2">
          Jump to
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-8 gap-2">
          <NavTile to="/" label="All hubs" icon={LayoutGrid} />
          <NavTile to="/projects" label="Projects" icon={FolderKanban} />
          {/* James, 21 Aug 2026: "currently the only way to get to schedule is
              in the drop-down nav on the left." For the screen that plans who is
              where, that is too far to reach. */}
          <NavTile to="/schedule" label="Schedule" icon={CalendarDays} />
          {/* Commercial surfaces gated to manager+ — field crew don't need to
              see the BID pipeline / client list (James 2026-04-30). */}
          {canManage && <NavTile to="/bids"    label="BIDs / RFQs" icon={ClipboardList} />}
          {/* T-508 Phase 4 — Clients is commercial, it now lives on the Finance
              hub. Hours came the other way: man-hours are operational, not cost. */}
          {canManage && <NavTile to="/reports/man-hours" label="Hours" icon={Clock} />}
          <NavTile to="/sites" label="Sites" icon={MapPin} />
          <NavTile to="/mops" label="MOPs" icon={FileText} />
          <NavTile to="/acceptance" label="Acceptance" icon={ShieldCheck} />
        </div>
      </div>

      {/* KPI strip — 6 tiles, dense info, accent pops where it matters */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
        <KPI label="Active projects"  value={kpis?.active   ?? "—"} icon={FolderKanban} accent={(kpis?.active ?? 0) > 0} />
        <KPI label="BIDs in flight"   value={kpis?.bids     ?? "—"} icon={ClipboardList} sub="open + submitted" />
        <KPI label="Quotes pending"   value={kpis?.quotes   ?? "—"} icon={FileText}     sub="awaiting client" />
        <KPI label="Bookings this week" value={kpis?.bookings ?? "—"} icon={Calendar}   sub="next 7 days" accent={(kpis?.bookings ?? 0) > 0} />
        <KPI label="HOPs awaiting"    value={kpis?.hops     ?? "—"} icon={ShieldCheck}  sub="manager review" accent={(kpis?.hops ?? 0) > 0} />
        <KPI label="AP backlog"       value={kpis?.apBacklog ?? "—"} icon={Banknote}    sub="pending receipts" accent={(kpis?.apBacklog ?? 0) > 0} />
      </div>

      {/* Two-column main row: This week + Active projects */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        {/* This week (left) */}
        <Card className="lg:col-span-1">
          <div className="p-4">
            <SectionHeader icon={Calendar} title="This week" sub="bookings + deadlines" />
            {upcomingBookings.length === 0 ? (
              <div className="py-8 text-center text-[12px] text-text-muted italic">
                No bookings scheduled in the next 14 days.
              </div>
            ) : (
              <div className="space-y-2 mt-3">
                {upcomingBookings.map((b: any) => <UpcomingBookingRow key={b.booking_id} booking={b} />)}
              </div>
            )}
          </div>
        </Card>

        {/* Active projects (right) */}
        <Card className="lg:col-span-2">
          <div className="p-4">
            <SectionHeader
              icon={FolderKanban}
              title="Active projects"
              sub={`${filteredActive.length} of ${projFilter === "all" ? activeProjects.length : (activeCounts.get(projFilter) ?? 0)} shown`}
              action={<Link to="/projects" className="text-[11px] text-accent hover:underline">View all →</Link>}
            />

            {/* Quick filter — mobilising / live / acceptance pending, the three
                statuses this widget holds. A status with nothing in it is still
                shown, reading 0, so the row doesn't reshuffle as work moves. */}
            {activeProjects.length > 0 && (
              <div className="mt-2 inline-flex flex-wrap gap-1 border border-line rounded-md bg-ink-raised p-0.5">
                <StatusChip label="All" count={activeProjects.length}
                  active={projFilter === "all"} onClick={() => setProjFilter("all")} />
                {ACTIVE_STATUSES.map(s => (
                  <StatusChip
                    key={s}
                    label={STATUS_LABEL[s]}
                    count={activeCounts.get(s) ?? 0}
                    active={projFilter === s}
                    onClick={() => setProjFilter(s)}
                  />
                ))}
              </div>
            )}

            {filteredActive.length === 0 ? (
              activeProjects.length > 0 ? (
                <div className="py-8 text-center text-[12px] text-text-muted italic">
                  Nothing {STATUS_LABEL[projFilter as ProjectStatus]} right now.
                </div>
              ) : (
                <div className="py-8 text-center">
                  <div className="text-[12px] text-text-muted italic mb-3">No active projects yet.</div>
                  {canManage && (
                    <Link to="/projects/new">
                      <Button variant="primary" size="sm" icon={Plus}>Create your first project</Button>
                    </Link>
                  )}
                </div>
              )
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-3">
                {filteredActive.map((p: any) => <ActiveProjectCard key={p.project_id} project={p} />)}
              </div>
            )}
          </div>
        </Card>
      </div>

      {/* Map row — Coretel Group branded "National Operations" panel */}
      <Card className="mb-4 overflow-hidden">
        {/* Branded header — feels like a proper ops dashboard, not a widget */}
        <div className="bg-gradient-to-br from-accent/15 via-accent/5 to-transparent border-b border-line p-5 md:p-6">
          <div className="flex items-end justify-between gap-3 flex-wrap">
            <div>
              <div className="font-semibold tracking-[0.18em] text-accent text-[20px] md:text-[24px] leading-none">
                CORETEL <span className="text-text">GROUP</span>
              </div>
              <div className="mt-2 flex items-center gap-3">
                <div className="h-0.5 w-8 bg-accent rounded-full" />
                <div className="text-text-muted text-[10px] md:text-[11px] tracking-wider2 uppercase font-mono">
                  National operations footprint
                </div>
              </div>
            </div>
            <Link
              to="/sites"
              className="text-[11px] text-text-muted hover:text-accent inline-flex items-center gap-1 font-medium transition-colors"
            >
              View all sites <ChevronRight size={12}/>
            </Link>
          </div>
        </div>

        {/* Map body */}
        <div className="p-4">
          <SitesMapWidget />
        </div>
      </Card>

      {/* Bottom row: Status chart + Recent activity */}
      {/* T-496 — the donut was mostly "closed" and the HOP feed was eight identical
          lines from a workflow Coretel does not actually run. Both replaced with
          things built on data the business generates every day: the live defect
          register, and crew days on site straight from timesheets. */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 mb-4">
        <DeliveryPipelineCard kpis={kpis} />
        <DefectsCard />
        <SiteActivityCard />
      </div>

      {/* The fortnight strip goes LAST. James, 22 Aug 2026: "put it below the
          widgets so this is the last one, so we can see it" — a horizontal strip
          reads best with nothing under it, and being last means scrolling to the
          bottom lands on it rather than past it. (His "page edit to move widgets
          around" idea is noted as later polish.) */}
      <ScheduleStripWidget />
    </div>
  );
}

// =============================================================================
// T-496 — the two panels under the map.
// =============================================================================

// Stages in the order work actually moves through them. "closed" is deliberately
// last and muted: 14 of 24 projects are closed, which made the old donut mostly
// grey and told you nothing about the live business.
const PIPELINE_STAGES: { key: string; label: string; statuses: ProjectStatus[]; colour: string; to: string }[] = [
  { key: "bidding",    label: "Bidding / quoted", statuses: ["bid", "quoted"],                colour: "#60A5FA", to: "/bids" },
  { key: "won",        label: "Won, not started", statuses: ["won"],                          colour: "#34D399", to: "/projects" },
  { key: "delivering", label: "Delivering",       statuses: ["mobilising", "live"],           colour: "#F97316", to: "/projects" },
  { key: "acceptance", label: "In acceptance",    statuses: ["acceptance_pending"],           colour: "#22D3EE", to: "/acceptance" },
  { key: "held",       label: "On hold",          statuses: ["on_hold"],                      colour: "#A78BFA", to: "/projects" },
  { key: "closed",     label: "Closed",           statuses: ["closed"],                       colour: "#6B7280", to: "/projects" },
];

function DeliveryPipelineCard({ kpis }: { kpis: any }) {
  const dist: Array<{ status: ProjectStatus; count: number }> = kpis?.statusDistribution ?? [];
  const countFor = (st: ProjectStatus[]) =>
    dist.filter(d => st.includes(d.status)).reduce((s, d) => s + d.count, 0);

  const rows = PIPELINE_STAGES.map(s => ({ ...s, count: countFor(s.statuses) })).filter(r => r.count > 0);
  const liveTotal = rows.filter(r => r.key !== "closed").reduce((s, r) => s + r.count, 0);
  const max = Math.max(1, ...rows.map(r => r.count));

  return (
    <Card className="lg:col-span-1">
      <div className="p-4">
        <SectionHeader icon={TrendingUp} title="Delivery pipeline" sub="where the work is"
          action={<Link to="/projects" className="text-[11px] text-accent hover:underline">Projects →</Link>} />

        {rows.length === 0 ? (
          <div className="py-12 text-center text-[12px] text-text-muted italic">No projects yet.</div>
        ) : (
          <>
            <div className="mt-3 flex items-baseline gap-2">
              <span className="text-2xl font-semibold text-text tracking-tight">{liveTotal}</span>
              <span className="text-[12px] text-text-muted">in flight</span>
            </div>

            {/* One proportional band per stage — reads left to right like the
                job actually progresses, which a pie cannot show. */}
            <div className="mt-3 flex h-2 rounded-full overflow-hidden bg-ink">
              {rows.filter(r => r.key !== "closed").map(r => (
                <div key={r.key} style={{ flexGrow: r.count, background: r.colour }} title={`${r.label}: ${r.count}`} />
              ))}
            </div>

            <div className="mt-4 space-y-2.5">
              {rows.map(r => (
                <Link key={r.key} to={r.to} className="block group">
                  <div className="flex items-center gap-2 text-[11px]">
                    <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: r.colour }} />
                    <span className={`truncate ${r.key === "closed" ? "text-text-dim" : "text-text-muted"} group-hover:text-accent transition-colors`}>
                      {r.label}
                    </span>
                    <span className="ml-auto font-mono text-text">{r.count}</span>
                  </div>
                  <div className="mt-1 h-1 rounded-full bg-ink overflow-hidden">
                    <div className="h-full rounded-full"
                      style={{ width: `${(r.count / max) * 100}%`, background: r.colour, opacity: r.key === "closed" ? 0.35 : 1 }} />
                  </div>
                </Link>
              ))}
            </div>
          </>
        )}
      </div>
    </Card>
  );
}

// -----------------------------------------------------------------------------
// Defects. A live register that IS used, unlike the pack-submission workflow.
// Severity is the headline because a critical defect is a different animal from
// a cosmetic one, and an overdue critical is the thing you want shouted at you.
const DEFECT_SEVERITY = [
  { key: "critical", label: "Critical", colour: "#EF4444" },
  { key: "major",    label: "Major",    colour: "#F97316" },
  { key: "minor",    label: "Minor",    colour: "#FBBF24" },
];

function DefectsCard() {
  const { data, isLoading } = useQuery({
    queryKey: ["project_hub_defects"],
    queryFn: async () => {
      const { data: rows } = await supabase
        .from("site_defects")
        .select("defect_id, defect_number, title, description, category, severity, status, due_date, raised_at, project:project_id(project_code, project_name)")
        .in("status", ["open", "in_progress", "awaiting_reinspection"])
        .order("raised_at", { ascending: true })
        .limit(60);

      const list = (rows ?? []) as any[];
      const today = new Date().toISOString().slice(0, 10);
      const bySeverity = DEFECT_SEVERITY.map(sv => ({
        ...sv, count: list.filter(d => d.severity === sv.key).length,
      }));
      const overdue = list.filter(d => d.due_date && d.due_date < today);
      const rank = { critical: 0, major: 1, minor: 2 } as Record<string, number>;
      const worst = [...list].sort((a, b) => {
        const od = (x: any) => (x.due_date && x.due_date < today ? 0 : 1);
        return od(a) - od(b) || (rank[a.severity] ?? 9) - (rank[b.severity] ?? 9);
      });
      return { total: list.length, bySeverity, overdue: overdue.length, worst: worst.slice(0, 5), today };
    },
  });

  const max = Math.max(1, ...(data?.bySeverity ?? []).map(s => s.count));

  return (
    <Card className="lg:col-span-1">
      <div className="p-4">
        <SectionHeader icon={AlertTriangle} title="Open defects" sub="across all sites"
          action={<Link to="/acceptance/defects" className="text-[11px] text-accent hover:underline">Defect centre →</Link>} />

        {isLoading ? (
          <div className="py-12 text-center text-[12px] text-text-muted italic">Loading…</div>
        ) : !data || data.total === 0 ? (
          <div className="py-12 text-center text-[12px] text-ok italic">No open defects. Enjoy it.</div>
        ) : (
          <>
            <div className="mt-3 flex items-baseline gap-3 flex-wrap">
              <span className="text-2xl font-semibold text-text tracking-tight">{data.total}</span>
              <span className="text-[12px] text-text-muted">open</span>
              {data.overdue > 0 && (
                <span className="text-[12px] font-medium text-danger ml-auto">{data.overdue} past due</span>
              )}
            </div>

            <div className="mt-3 space-y-2">
              {data.bySeverity.map(sv => (
                <div key={sv.key}>
                  <div className="flex items-center gap-2 text-[11px]">
                    <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: sv.colour }} />
                    <span className="text-text-muted">{sv.label}</span>
                    <span className="ml-auto font-mono text-text">{sv.count}</span>
                  </div>
                  <div className="mt-1 h-1.5 rounded-full bg-ink overflow-hidden">
                    <div className="h-full rounded-full transition-all"
                      style={{ width: `${(sv.count / max) * 100}%`, background: sv.colour }} />
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-3 pt-3 border-t border-line space-y-1.5">
              {data.worst.map((d: any) => {
                const late = d.due_date && d.due_date < data.today;
                const sv = DEFECT_SEVERITY.find(x => x.key === d.severity);
                return (
                  <Link key={d.defect_id} to="/acceptance/defects" className="block group">
                    <div className="flex items-center gap-2 text-[11px]">
                      <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: sv?.colour ?? "#6B7280" }} />
                      <span className="text-text truncate group-hover:text-accent transition-colors">
                        {d.title || d.description}
                      </span>
                      {late && <span className="shrink-0 text-[10px] font-mono text-danger">overdue</span>}
                    </div>
                    <div className="text-[10px] text-text-dim truncate pl-3.5">
                      {d.project?.project_code ?? "—"} · {d.category}
                    </div>
                  </Link>
                );
              })}
            </div>
          </>
        )}
      </div>
    </Card>
  );
}

// -----------------------------------------------------------------------------
// Site activity. Answers "where did our hours actually go", from timesheets —
// the one record that gets filled in every week, so it is the most honest
// picture of what the business did.
//
// Two controls: the window (how far back) and the split (what the bars group by).
// Hours are booked hours against a project; yard and OPEX days do not appear
// because they carry no project.
const ACTIVITY_WINDOWS = [
  { key: "week",    label: "Week",     days: 7 },
  { key: "fortnight", label: "Fortnight", days: 14 },
  { key: "month",   label: "Month",    days: 30 },
  { key: "quarter", label: "Quarter",  days: 91 },
];
const ACTIVITY_SPLITS = [
  { key: "project", label: "Project" },
  { key: "client",  label: "Client" },
  { key: "carrier", label: "Carrier" },
  { key: "crew",    label: "Crew" },
];
const ACTIVITY_COLOURS = ["#F97316", "#22D3EE", "#34D399", "#FBBF24", "#A78BFA", "#60A5FA", "#F472B6"];

function SiteActivityCard() {
  const [windowKey, setWindowKey] = useState("month");
  const [splitKey, setSplitKey]   = useState("project");
  const win = ACTIVITY_WINDOWS.find(w => w.key === windowKey) ?? ACTIVITY_WINDOWS[2];

  const { data, isLoading } = useQuery({
    queryKey: ["project_hub_site_activity_v2", windowKey, splitKey],
    queryFn: async () => {
      const from = new Date(Date.now() - (win.days - 1) * 86400000).toISOString().slice(0, 10);

      const { data: entries } = await supabase
        .from("timesheet_entries")
        .select("work_date, total_hours, project_id, employee_id")
        .gte("work_date", from)
        .not("project_id", "is", null)
        .limit(8000);
      const rows = (entries ?? []) as any[];

      const projectIds = Array.from(new Set(rows.map(r => r.project_id).filter(Boolean)));
      const labelFor = new Map<string, string>();

      if (projectIds.length) {
        if (splitKey === "project") {
          const { data: ps } = await supabase.from("projects")
            .select("project_id, project_code").in("project_id", projectIds);
          (ps ?? []).forEach((p: any) => labelFor.set(p.project_id, p.project_code ?? "—"));
        } else if (splitKey === "client") {
          const { data: ps } = await supabase.from("projects")
            .select("project_id, client:client_id(legal_name)").in("project_id", projectIds);
          (ps ?? []).forEach((p: any) => labelFor.set(p.project_id, p.client?.legal_name ?? "No client"));
        } else if (splitKey === "carrier") {
          const { data: ps } = await supabase.from("projects")
            // carriers has carrier_code ('NBN', 'TELSTRA') + legal_name — no
            // carrier_name column. The code is the better label anyway.
            .select("project_id, carrier:carrier_id(carrier_code, legal_name)").in("project_id", projectIds);
          (ps ?? []).forEach((p: any) =>
            labelFor.set(p.project_id, p.carrier?.carrier_code ?? p.carrier?.legal_name ?? "No carrier"));
        }
      }

      // Crew splits by PERSON, not project — a crew works several jobs.
      const crewFor = new Map<string, string>();
      if (splitKey === "crew") {
        const empIds = Array.from(new Set(rows.map(r => r.employee_id).filter(Boolean)));
        if (empIds.length) {
          const { data: cm } = await supabase.from("crew_members")
            .select("employee_id, member_to, crew:crew_id(crew_name)")
            .in("employee_id", empIds)
            .is("member_to", null);
          (cm ?? []).forEach((m: any) => {
            if (m.crew?.crew_name) crewFor.set(m.employee_id, m.crew.crew_name);
          });
        }
      }

      // Buckets: a week or fortnight is read day by day, a month or quarter by
      // week. A 91-bar daily strip was the thing that looked like nothing.
      const daily = win.days <= 14;
      const bucketOf = (date: string) => {
        if (daily) return date;
        const d = new Date(date + "T00:00:00");
        const dow = (d.getDay() + 6) % 7;                    // Monday = 0
        return new Date(d.getTime() - dow * 86400000).toISOString().slice(0, 10);
      };

      const totals   = new Map<string, number>();
      const buckets  = new Map<string, { hours: number; people: Set<string>; projects: Set<string> }>();
      const people   = new Set<string>();
      let totalHours = 0;

      for (const r of rows) {
        const hrs = Number(r.total_hours ?? 0);
        totalHours += hrs;
        if (r.employee_id) people.add(r.employee_id);

        const bk = bucketOf(r.work_date);
        const e = buckets.get(bk) ?? { hours: 0, people: new Set<string>(), projects: new Set<string>() };
        e.hours += hrs;
        if (r.employee_id) e.people.add(r.employee_id);
        if (r.project_id)  e.projects.add(r.project_id);
        buckets.set(bk, e);

        const key = splitKey === "crew"
          ? (crewFor.get(r.employee_id) ?? "No crew")
          : (labelFor.get(r.project_id) ?? "—");
        totals.set(key, (totals.get(key) ?? 0) + hrs);
      }

      // Top 6 by hours; everything else collapses into one slice so the donut
      // always adds to 100% instead of quietly dropping the tail.
      const ranked = Array.from(totals.entries())
        .map(([name, hours]) => ({ name, hours }))
        .sort((a, b) => b.hours - a.hours);
      const top  = ranked.slice(0, 6);
      const rest = ranked.slice(6);
      const slices = rest.length
        ? [...top, { name: `${rest.length} other${rest.length === 1 ? "" : "s"}`, hours: rest.reduce((s, r) => s + r.hours, 0) }]
        : top;

      // Fill every bucket in the window, including empty ones — a gap IS the
      // information when you are looking for quiet weeks.
      const series: any[] = [];
      const seen = new Set<string>();
      for (let i = win.days - 1; i >= 0; i--) {
        const day = new Date(Date.now() - i * 86400000).toISOString().slice(0, 10);
        const bk  = bucketOf(day);
        if (seen.has(bk)) continue;
        seen.add(bk);
        const e = buckets.get(bk);
        series.push({
          bucket:   bk,
          label:    daily ? format(parseISO(bk), "EEE d") : format(parseISO(bk), "d MMM"),
          hours:    Math.round((e?.hours ?? 0) * 10) / 10,
          people:   e?.people.size ?? 0,
          projects: e?.projects.size ?? 0,
          daily,
        });
      }

      const best = series.reduce((b, x) => (x.hours > (b?.hours ?? 0) ? x : b), null as any);
      return {
        slices, series, totalHours, daily,
        people: people.size,
        distinct: ranked.length,
        busiest: best,
      };
    },
  });

  return (
    <Card className="lg:col-span-2">
      <div className="p-4">
        <SectionHeader icon={Activity} title="Site activity" sub="booked hours from timesheets"
          action={<Link to="/timesheets" className="text-[11px] text-accent hover:underline">Timesheets →</Link>} />

        <div className="mt-3 flex items-center gap-2 flex-wrap">
          <Segmented options={ACTIVITY_WINDOWS.map(w => ({ key: w.key, label: w.label }))}
            value={windowKey} onChange={setWindowKey} />
          <span className="text-[10px] text-text-dim">split by</span>
          <Segmented options={ACTIVITY_SPLITS} value={splitKey} onChange={setSplitKey} />
        </div>

        {isLoading ? (
          <div className="py-16 text-center text-[12px] text-text-muted italic">Loading…</div>
        ) : !data || data.totalHours === 0 ? (
          <div className="py-16 text-center text-[12px] text-text-muted italic">
            No booked hours against a project in this {win.label.toLowerCase()}.
          </div>
        ) : (
          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-5">

            {/* Donut: the split, with the total living in the hole. */}
            <div>
              <div className="text-[10px] uppercase tracking-wider2 text-text-dim mb-1">
                Hours by {ACTIVITY_SPLITS.find(x => x.key === splitKey)?.label.toLowerCase()}
              </div>
              <div className="relative h-[150px]">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={data.slices} dataKey="hours" nameKey="name"
                      cx="50%" cy="50%" innerRadius={46} outerRadius={68} paddingAngle={2} stroke="none">
                      {data.slices.map((_, i) => (
                        <Cell key={i} fill={ACTIVITY_COLOURS[i % ACTIVITY_COLOURS.length]} />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{ background: "#14161a", border: "1px solid #2a2e35", borderRadius: 6, fontSize: 12, padding: "6px 10px" }}
                    itemStyle={{ color: "#f5f5f5", fontSize: 12 }}
                    labelStyle={{ color: "#9aa0a6", fontSize: 11, marginBottom: 2 }}
                      formatter={(v: any, n: any) => [
                        `${Math.round(Number(v)).toLocaleString()} hours · ${((Number(v) / data.totalHours) * 100).toFixed(0)}% of the ${win.label.toLowerCase()}`,
                        n]} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                  <span className="text-xl font-semibold text-text tracking-tight leading-none">
                    {Math.round(data.totalHours).toLocaleString()}
                  </span>
                  <span className="text-[10px] text-text-dim mt-0.5">hours</span>
                </div>
              </div>

              {/* Kept narrow and centred under the donut — spread across the full
                  column the labels and their numbers ended up a mile apart. */}
              <div className="mt-2 mx-auto max-w-[230px] space-y-1.5">
                {data.slices.map((sl, i) => (
                  <div key={sl.name} className="flex items-center gap-2 text-[12px]">
                    <span className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                      style={{ background: ACTIVITY_COLOURS[i % ACTIVITY_COLOURS.length] }} />
                    <span className="text-text truncate">{sl.name}</span>
                    <span className="ml-auto font-mono text-text shrink-0">
                      {Math.round(sl.hours).toLocaleString()}h
                    </span>
                    <span className="font-mono text-text-dim shrink-0 w-8 text-right">
                      {((sl.hours / data.totalHours) * 100).toFixed(0)}%
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Bars: when the hours happened. Hover gives the detail. */}
            <div>
              <div className="text-[10px] uppercase tracking-wider2 text-text-dim mb-1">
                {data.daily ? "Hours per day" : "Hours per week"}
              </div>
              <div className="h-[150px] -ml-3">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data.series} margin={{ top: 6, right: 6, bottom: 0, left: 6 }}>
                    <XAxis dataKey="label" tick={{ fontSize: 9, fill: "#9aa0a6" }}
                      axisLine={false} tickLine={false} interval="preserveStartEnd" />
                    <Tooltip
                      cursor={{ fill: "#ffffff08" }}
                      contentStyle={{ background: "#14161a", border: "1px solid #2a2e35", borderRadius: 6, fontSize: 12, padding: "6px 10px" }}
                    itemStyle={{ color: "#f5f5f5", fontSize: 12 }}
                    labelStyle={{ color: "#9aa0a6", fontSize: 11, marginBottom: 2 }}
                      labelFormatter={(l: any, pl: any) => {
                        const d = pl?.[0]?.payload;
                        if (!d) return l;
                        return d.daily
                          ? format(parseISO(d.bucket), "EEEE d MMM")
                          : `Week of ${format(parseISO(d.bucket), "d MMM")}`;
                      }}
                      formatter={(_v: any, _n: any, pr: any) => {
                        const d = pr?.payload ?? {};
                        return [
                          `${Number(d.hours).toLocaleString()}h · ${d.people} on site · ${d.projects} job${d.projects === 1 ? "" : "s"}`,
                          "",
                        ];
                      }} />
                    <Bar dataKey="hours" radius={[3, 3, 0, 0]} fill="#F97316" />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              <div className="mt-2 grid grid-cols-3 gap-2">
                <MiniStat label="On the tools" value={String(data.people)} />
                <MiniStat label={data.daily ? "Busiest day" : "Busiest week"}
                  value={data.busiest && data.busiest.hours > 0
                    ? `${Math.round(data.busiest.hours)}h`
                    : "—"} />
                <MiniStat label={ACTIVITY_SPLITS.find(x => x.key === splitKey)?.label ?? ""}
                  value={String(data.distinct)} />
              </div>
            </div>
          </div>
        )}
      </div>
    </Card>
  );
}

function MiniStat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded border border-line bg-ink px-2 py-1.5">
      <div className="text-[14px] font-semibold text-text leading-none">{value}</div>
      <div className="text-[9px] uppercase tracking-wider2 text-text-dim mt-1 truncate">{label}</div>
    </div>
  );
}

// Small pill toggle used by the activity controls.
function Segmented({ options, value, onChange }: {
  options: { key: string; label: string }[];
  value: string;
  onChange: (k: string) => void;
}) {
  return (
    <div className="inline-flex rounded border border-line overflow-hidden">
      {options.map(o => (
        <button key={o.key} type="button" onClick={() => onChange(o.key)}
          className={`px-2 py-1 text-[10px] transition-colors ${
            value === o.key ? "bg-accent text-white" : "bg-ink text-text-muted hover:bg-ink-hover"}`}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

// -----------------------------------------------------------------------------
// =============================================================================
// ScheduleStripWidget — the next fortnight, on the command centre.
// =============================================================================
// James, 21 Aug 2026: "I think we need to add schedule to this heading nav, and
// maybe even a calendar view with an option to pick which one, below the
// Australian map, as a visual widget on this screen."
//
// A FORTNIGHT, not a month. This is a dashboard strip, not the schedule - the
// question it has to answer in one glance is "is anything about to go wrong in
// the next two weeks", and a month squeezed into a card answers nothing legibly.
// The full month, drag-and-drop and the people layer all live on /schedule, and
// every bar here is a link to it.
//
// Two ways to slice it, because the same fortnight answers two questions:
//   Crews — who is busy, and who is free (a gap in a row is available capacity)
//   Sites — what is running where, and where nobody is booked
// =============================================================================
const STRIP_DAYS = 14;

function ScheduleStripWidget() {
  const [mode, setMode] = useState<"crews" | "sites">("crews");
  // James, 22 Aug 2026: "add filters here to select / de-select crew to clean up
  // the view." Stored as the crews turned OFF, so a crew created next week is
  // visible by default rather than silently hidden by a stale filter.
  const [hiddenCrews, setHiddenCrews] = useState<Set<string>>(new Set());
  const toggleCrew = (id: string) =>
    setHiddenCrews(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });

  const start = useMemo(() => startOfDay(new Date()), []);
  const startKey = format(start, "yyyy-MM-dd");
  const endKey   = format(addDays(start, STRIP_DAYS - 1), "yyyy-MM-dd");

  const { data: crews = [] } = useQuery({
    queryKey: ["hub_strip_crews"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("crews")
        .select("crew_id, crew_code, crew_name, colour_hex")
        .eq("active", true)
        .order("crew_name");
      if (error) throw error;
      return (data ?? []) as any[];
    },
  });

  const { data: assignments = [] } = useQuery({
    queryKey: ["hub_strip_pcas", startKey, endKey],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("project_crew_assignments")
        .select(`
          pca_id, project_id, crew_id, assigned_from, assigned_to,
          crews:crew_id ( crew_id, crew_code, crew_name, colour_hex ),
          projects:project_id ( project_id, project_code, project_name, sites:site_id ( site_name ) )
        `)
        .lte("assigned_from", endKey)
        .or(`assigned_to.is.null,assigned_to.gte.${startKey}`);
      if (error) throw error;
      return (data ?? []) as any[];
    },
  });

  // Promoted key dates tint the day column, exactly as they do on the schedule,
  // so the two screens do not teach two different visual languages.
  const { data: keyDates = [] } = useQuery({
    queryKey: ["hub_strip_keydates", startKey, endKey],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("project_bookings")
        .select("booking_id, title, booking_type, scheduled_start, scheduled_end, customer_impacting")
        .eq("show_on_schedule", true)
        .neq("status", "cancelled")
        .lte("scheduled_start", `${endKey}T23:59:59`);
      if (error) throw error;
      return (data ?? []) as any[];
    },
  });

  const days = useMemo(
    () => Array.from({ length: STRIP_DAYS }, (_, i) => addDays(start, i)),
    [start],
  );

  const keyByDay = useMemo(() => {
    const m = new Map<string, any[]>();
    for (const b of keyDates) {
      const f = String(b.scheduled_start).slice(0, 10);
      const t0 = String(b.scheduled_end ?? b.scheduled_start).slice(0, 10);
      const t = t0 < f ? f : t0;
      for (const d of days) {
        const k = format(d, "yyyy-MM-dd");
        if (k >= f && k <= t) (m.get(k) ?? m.set(k, []).get(k)!).push(b);
      }
    }
    return m;
  }, [keyDates, days]);

  const idxOf = (key: string) => {
    if (key <= startKey) return 0;
    if (key >= endKey)   return STRIP_DAYS - 1;
    return Math.max(0, Math.min(STRIP_DAYS - 1,
      Math.round((parseISO(key).getTime() - start.getTime()) / 86_400_000)));
  };

  type Bar = { key: string; from: number; span: number; colour: string; label: string; title: string };

  // One row per crew, or one per site, from the same assignment rows.
  const rows = useMemo(() => {
    const live = assignments.filter(a => {
      const f = a.assigned_from as string;
      const t = (a.assigned_to ?? "9999-12-31") as string;
      return f <= endKey && t >= startKey && !hiddenCrews.has(a.crew_id as string);
    });

    const mk = (a: any): Bar => {
      const f = a.assigned_from as string;
      const t = (a.assigned_to ?? endKey) as string;
      const from = idxOf(f);
      const to   = idxOf(t < f ? f : t);
      const site = a.projects?.sites?.site_name ?? a.projects?.project_name ?? "Job";
      return {
        key: a.pca_id,
        from, span: Math.max(1, to - from + 1),
        colour: a.crews?.colour_hex ?? "#475569",
        label: mode === "crews" ? site : (a.crews?.crew_code ?? "crew"),
        title: `${site} · ${a.crews?.crew_name ?? "crew"} · ${fmtStripDate(f)} → ${a.assigned_to ? fmtStripDate(t) : "open ended"}`,
      };
    };

    if (mode === "crews") {
      return crews.filter(c => !hiddenCrews.has(c.crew_id)).map(c => ({
        id: c.crew_id,
        name: c.crew_name,
        sub: c.crew_code,
        dot: c.colour_hex,
        bars: live.filter(a => a.crew_id === c.crew_id).map(mk),
      }));
    }

    const bySite = new Map<string, { id: string; name: string; sub: string; dot: string; bars: Bar[] }>();
    for (const a of live) {
      const pid  = a.project_id as string;
      const name = a.projects?.sites?.site_name ?? a.projects?.project_name ?? "Job";
      const row = bySite.get(pid) ?? {
        id: pid, name, sub: a.projects?.project_code ?? "",
        dot: a.crews?.colour_hex ?? "#475569", bars: [] as Bar[],
      };
      row.bars.push(mk(a));
      bySite.set(pid, row);
    }
    return [...bySite.values()].sort((x, z) => x.name.localeCompare(z.name));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assignments, crews, mode, startKey, endKey, hiddenCrews]);

  const busyCount = rows.filter(r => r.bars.length > 0).length;

  return (
    <Card className="mb-4 overflow-hidden">
      <div className="flex items-center justify-between gap-3 flex-wrap px-4 py-3 border-b border-line">
        <div className="flex items-center gap-2 min-w-0">
          <CalendarDays size={14} className="text-accent flex-shrink-0"/>
          <div className="min-w-0">
            <div className="text-[13px] font-medium text-text">Next {STRIP_DAYS} days</div>
            <div className="text-[11px] text-text-muted truncate">
              {mode === "crews"
                ? `${busyCount} of ${rows.length} crews booked`
                : `${rows.length} job${rows.length === 1 ? "" : "s"} with a crew on site`}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* "an option to pick which one" */}
          <div className="inline-flex rounded border border-line overflow-hidden text-[11px]">
            {(["crews", "sites"] as const).map(m => (
              <button key={m} type="button" onClick={() => setMode(m)}
                className={`px-2.5 py-1 transition-colors ${
                  mode === m ? "bg-accent text-white" : "text-text-muted hover:text-text hover:bg-ink-hover"}`}>
                {m === "crews" ? "Crews" : "Sites"}
              </button>
            ))}
          </div>
          <Link to="/schedule"
            className="text-[11px] text-text-muted hover:text-accent inline-flex items-center gap-1 font-medium transition-colors">
            Open schedule <ChevronRight size={12}/>
          </Link>
        </div>
      </div>

      {/* Crew filter chips — click to hide/show a crew's row. Dimmed = hidden. */}
      {crews.length > 1 && (
        <div className="flex items-center gap-1.5 flex-wrap px-4 py-2 border-b border-line/50">
          {crews.map((c: any) => {
            const off = hiddenCrews.has(c.crew_id);
            return (
              <button key={c.crew_id} type="button" onClick={() => toggleCrew(c.crew_id)}
                title={off ? `Show ${c.crew_name}` : `Hide ${c.crew_name}`}
                className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[11px] transition-opacity ${
                  off ? "border-line/50 opacity-40" : "border-line"}`}>
                <span className="inline-block w-2 h-2 rounded-full" style={{ background: c.colour_hex }}/>
                <span className="font-mono">{c.crew_code}</span>
              </button>
            );
          })}
          {hiddenCrews.size > 0 && (
            <button type="button" onClick={() => setHiddenCrews(new Set())}
              className="text-[11px] text-accent hover:underline ml-1">Show all</button>
          )}
        </div>
      )}

      <div className="p-4 overflow-x-auto">
        <div className="min-w-[560px]">
          {/* Day header */}
          <div className="grid items-end gap-px mb-1"
            style={{ gridTemplateColumns: `150px repeat(${STRIP_DAYS}, minmax(0, 1fr))` }}>
            <div className="text-[10px] uppercase tracking-wider2 text-text-muted">
              {mode === "crews" ? "crew" : "site"}
            </div>
            {days.map((d, i) => {
              const weekend = d.getDay() === 0 || d.getDay() === 6;
              const isNow = isToday(d);
              return (
                <div key={i} className="text-center">
                  <div className={`text-[9px] ${weekend ? "text-text-dim" : "text-text-muted"}`}>
                    {format(d, "EEEEE")}
                  </div>
                  <div className={`text-[10px] font-mono ${
                    isNow ? "text-accent font-semibold" : weekend ? "text-text-dim" : "text-text"}`}>
                    {format(d, "d")}
                  </div>
                </div>
              );
            })}
          </div>

          {rows.length === 0 && (
            <div className="text-[12px] text-text-muted italic py-4 text-center">
              Nothing booked in the next {STRIP_DAYS} days.
            </div>
          )}

          {rows.map(r => (
            <div key={r.id} className="grid items-center gap-px py-0.5"
              style={{ gridTemplateColumns: `150px repeat(${STRIP_DAYS}, minmax(0, 1fr))` }}>
              <div className="min-w-0 pr-2 flex items-center gap-1.5">
                <span className="inline-block w-2 h-2 rounded-full flex-shrink-0" style={{ background: r.dot }}/>
                <span className="text-[11px] text-text truncate">{r.name}</span>
              </div>

              <div className="relative h-[18px]"
                style={{ gridColumn: `2 / span ${STRIP_DAYS}` }}>
                {/* Weekend shading + key-date tint, behind the bars. */}
                {days.map((d, i) => {
                  const weekend = d.getDay() === 0 || d.getDay() === 6;
                  const k = format(d, "yyyy-MM-dd");
                  const kd = keyByDay.get(k);
                  if (!weekend && !kd) return null;
                  return (
                    <div key={`bg-${i}`} className="absolute top-0 bottom-0 pointer-events-none"
                      style={{
                        left:  `${(i / STRIP_DAYS) * 100}%`,
                        width: `${(1 / STRIP_DAYS) * 100}%`,
                        background: kd
                          ? "rgba(124,58,237,0.14)"
                          : "rgba(255,255,255,0.035)",
                      }}/>
                  );
                })}

                {r.bars.length === 0 && (
                  <div className="absolute inset-0 flex items-center">
                    <span className="text-[9px] text-text-dim italic">Free</span>
                  </div>
                )}

                {r.bars.map(b => (
                  <Link key={b.key} to="/schedule" title={b.title}
                    className="absolute rounded-sm px-1.5 text-[9px] leading-[16px] truncate font-medium
                               text-white/95 hover:brightness-110 transition"
                    style={{
                      top: 1, height: 16,
                      left:  `${(b.from / STRIP_DAYS) * 100}%`,
                      width: `calc(${(b.span / STRIP_DAYS) * 100}% - 2px)`,
                      background: b.colour,
                    }}>
                    {b.span > 1 ? b.label : ""}
                  </Link>
                ))}

                {/* Today. The strip starts today, so this is always the left edge
                    - it is here to anchor the eye, not to locate a date. */}
                <div className="absolute top-0 bottom-0 border-l border-accent/60 pointer-events-none" style={{ left: 0 }} />
              </div>
            </div>
          ))}

          {keyByDay.size > 0 && (
            <div className="mt-2 pt-2 border-t border-line/40 flex items-center gap-2 text-[10px] text-text-muted">
              <span className="inline-block w-3 h-2.5 rounded-sm" style={{ background: "rgba(124,58,237,0.30)" }}/>
              <span>
                shaded day = key date from a project
                {" · "}
                {[...keyByDay.values()].flat()
                  .filter((b, i, a) => a.findIndex(x => x.booking_id === b.booking_id) === i)
                  .slice(0, 3)
                  .map(b => b.title).join(", ")}
              </span>
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}

function fmtStripDate(d: string): string {
  try { return format(parseISO(d), "d MMM"); } catch { return d; }
}

// One quick-filter chip on the Active projects widget. Count sits in the chip
// so you can see where the work is without pressing anything.
function StatusChip({ label, count, active, onClick }: {
  label: string; count: number; active: boolean; onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-2.5 py-1 rounded text-[11px] capitalize transition-colors ${
        active ? "bg-accent text-white" : "text-text-muted hover:text-text"
      }`}
    >
      {label} <span className={`font-mono ${active ? "text-white/80" : "text-text-dim"}`}>{count}</span>
    </button>
  );
}

function NavTile({ to, label, icon: Icon }: { to: string; label: string; icon: any }) {
  return (
    <Link
      to={to}
      className="group inline-flex items-center gap-2 px-3 py-2.5 rounded bg-ink-raised border border-line text-accent hover:border-accent hover:bg-ink-hover transition-colors"
    >
      <Icon size={14} className="flex-shrink-0" />
      <span className="text-[12px] font-medium truncate">{label}</span>
    </Link>
  );
}

function SectionHeader({
  icon: Icon, title, sub, action,
}: {
  icon: any; title: string; sub?: string; action?: React.ReactNode;
}) {
  return (
    <div className="flex items-center justify-between gap-3">
      <div className="flex items-center gap-2 min-w-0">
        <Icon size={14} className="text-accent flex-shrink-0"/>
        <h3 className="text-[13px] font-semibold text-text">{title}</h3>
        {sub && <span className="text-[10px] font-mono text-text-dim hidden sm:inline">· {sub}</span>}
      </div>
      {action}
    </div>
  );
}

// -----------------------------------------------------------------------------
function UpcomingBookingRow({ booking }: { booking: any }) {
  const start = parseISO(booking.scheduled_start);
  const dayLabel = isToday(start) ? "Today"
                 : isTomorrow(start) ? "Tomorrow"
                 : format(start, "EEE dd MMM");

  const TYPE_ICON: Record<string, any> = {
    plant: Truck, delivery: Package, integration: ShieldCheck,
    subcontractor: Users, meeting: Calendar, inspection: FileText, other: Wrench,
  };
  const Icon = TYPE_ICON[booking.booking_type] ?? Wrench;

  return (
    <Link to={`/projects/${booking.project_id}/schedule`} className="block group">
      <div className="p-2.5 rounded bg-ink border border-line hover:border-accent transition-colors">
        <div className="flex items-start gap-2">
          <Icon size={13} className="text-accent flex-shrink-0 mt-0.5"/>
          <div className="flex-1 min-w-0">
            <div className="text-[12px] text-text font-medium truncate">{booking.title}</div>
            <div className="text-[10px] font-mono text-text-muted mt-0.5 flex items-center gap-2 flex-wrap">
              <span>{dayLabel} {format(start, "HH:mm")}</span>
              <span className="text-text-dim">·</span>
              <span className="truncate">{booking.project_code}</span>
            </div>
            {/* Site row — quick reference per James 2026-04-25 */}
            {(booking.site_name || booking.site_code) && (
              <div className="text-[10px] text-text-muted mt-0.5 truncate inline-flex items-center gap-1">
                <MapPin size={9} className="text-accent flex-shrink-0"/>
                <span className="truncate">
                  {booking.site_name ?? booking.site_code}
                  {booking.site_suburb && <span className="text-text-dim"> · {booking.site_suburb}</span>}
                </span>
              </div>
            )}
            {(booking.contact_employee_name || booking.supplier_name || booking.external_contact_name) && (
              <div className="text-[10px] text-text-dim mt-0.5 truncate">
                {booking.contact_employee_name ?? booking.supplier_name ?? booking.external_contact_name}
              </div>
            )}
          </div>
        </div>
      </div>
    </Link>
  );
}

// -----------------------------------------------------------------------------
function ActiveProjectCard({ project }: { project: any }) {
  return (
    <Link to={`/projects/${project.project_id}`} className="block group">
      <div className="p-3 rounded bg-ink border border-line hover:border-accent transition-colors">
        <div className="flex items-start justify-between gap-2 mb-1.5">
          <div className="text-[11px] font-mono text-text-muted truncate">
            {project.project_code}
          </div>
          <Pill label={STATUS_LABEL[project.status as ProjectStatus]} tone={STATUS_TONE[project.status as ProjectStatus]} small />
        </div>
        <div className="text-[13px] text-text font-medium line-clamp-1 mb-1">
          {project.project_name}
        </div>
        <div className="text-[10px] font-mono text-text-dim flex items-center gap-1.5 flex-wrap">
          {project.client?.legal_name && <span className="truncate inline-flex items-center gap-1"><Building2 size={9}/> {project.client.legal_name}</span>}
          {project.carrier?.carrier_code && <><span className="text-text-dim">·</span><span>{project.carrier.carrier_code}</span></>}
          {project.site?.site_code && <><span className="text-text-dim">·</span><span className="inline-flex items-center gap-0.5"><MapPin size={9}/> {project.site.site_code}</span></>}
        </div>
        {project.updated_at && (
          <div className="text-[10px] text-text-dim mt-1.5">
            updated {formatDistanceToNow(parseISO(project.updated_at), { addSuffix: true })}
          </div>
        )}
      </div>
    </Link>
  );
}

// -----------------------------------------------------------------------------
